import React from 'react';
import { useNavigate } from 'react-router-dom';
import { X, CheckCircle } from 'lucide-react';
import { trackTrialStarted } from '../../services/analytics';

const TrialModal = ({ 
  isDarkMode, 
  audienceMode, 
  email, 
  setEmail, 
  error, 
  modalLoading, 
  onSubmit, 
  onClose 
}) => {
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    trackTrialStarted(email, audienceMode);
    await onSubmit(e);
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="trial-modal">
      <div className={`max-w-md w-full rounded-2xl p-8 ${isDarkMode ? 'bg-[#131C2E] border border-slate-800' : 'bg-white'} shadow-2xl`}>
        <div className="flex justify-between items-start mb-6">
          <div>
            <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-3 ${audienceMode === 'teacher' ? (isDarkMode ? 'bg-emerald-900/50 text-emerald-400' : 'bg-emerald-100 text-emerald-700') : (isDarkMode ? 'bg-sky-900/50 text-sky-400' : 'bg-sky-100 text-sky-700')}`}>
              {audienceMode === 'teacher' ? 'Teacher Trial' : 'Student Trial'}
            </span>
            <h2 className={`text-2xl font-bold font-['Outfit'] ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
              Start Your Free Trial
            </h2>
          </div>
          <button onClick={onClose} className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-slate-800' : 'hover:bg-slate-100'}`}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className={`p-4 rounded-xl mb-6 ${isDarkMode ? 'bg-slate-800/50' : 'bg-purple-50'}`}>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>3 days of full premium access</span>
            </li>
            <li className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>Generate up to 3 quizzes per day</span>
            </li>
            <li className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>All formats: PDF, Image, Audio, Video, Text</span>
            </li>
            <li className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
              <span className={isDarkMode ? 'text-slate-300' : 'text-slate-700'}>No credit card required</span>
            </li>
          </ul>
        </div>

        {error && (
          <div className={`mb-4 p-3 rounded-lg text-sm ${isDarkMode ? 'bg-red-900/30 border border-red-800 text-red-400' : 'bg-red-50 border border-red-200 text-red-600'}`}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
              Enter your email to start
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="trial-email-input"
              className={`w-full px-4 py-3 rounded-xl border focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all ${
                isDarkMode 
                  ? 'bg-slate-800 border-slate-700 text-white placeholder-slate-500' 
                  : 'bg-white border-slate-300 text-slate-900 placeholder-slate-400'
              }`}
              placeholder="your@email.com"
              required
            />
          </div>

          <button
            type="submit"
            disabled={modalLoading}
            data-testid="trial-submit-button"
            className="w-full py-3 bg-gradient-to-r from-purple-600 to-violet-600 text-white rounded-xl font-bold hover:-translate-y-0.5 active:translate-y-0 transition-all disabled:opacity-50 disabled:hover:translate-y-0 shadow-lg shadow-purple-500/25"
          >
            {modalLoading ? 'Starting...' : 'Start Free Trial →'}
          </button>
        </form>

        <p className={`mt-4 text-xs text-center ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
          Already have an account?{' '}
          <button onClick={() => { onClose(); navigate('/login'); }} className="text-purple-500 hover:text-purple-400 font-medium">
            Log in
          </button>
        </p>
      </div>
    </div>
  );
};

export default TrialModal;
